import type { DayPlan, DayRow, ItineraryData } from '$/dto/ItineraryData.dto.ts';
import { getAccommodationStays } from '$/utils/itinerary/accommodationSchedule.ts';
import type { PartOfTheDay } from '$/_const/PartOfTheDay.ts';
import { allPartOfTheDay } from '$/_const/PartOfTheDay.ts';

export type ActivityChangeStatus = 'added' | 'replaced' | 'refined' | 'moved';

export type ItineraryChangeEntry =
  | { kind: 'day_added'; day: number; title: string }
  | { kind: 'day_removed'; day: number; title: string }
  | { kind: 'day_title'; day: number; title: string; previousTitle: string }
  | { kind: 'day_dining'; day: number }
  | { kind: 'day_local_secret'; day: number }
  | { kind: 'activity_added'; day: number; partOfTheDay: PartOfTheDay; title: string }
  | { kind: 'activity_replaced'; day: number; partOfTheDay: PartOfTheDay; title: string; previousTitle: string }
  | { kind: 'activity_refined'; day: number; partOfTheDay: PartOfTheDay; title: string }
  | {
      kind: 'activity_moved';
      day: number;
      partOfTheDay: PartOfTheDay;
      previousPartOfTheDay: PartOfTheDay;
      title: string;
    }
  | { kind: 'activity_removed'; day: number; partOfTheDay: PartOfTheDay; title: string }
  | { kind: 'accommodation_replaced'; place: string; previousPlace: string }
  | { kind: 'accommodation_refined'; place: string };

export type ItineraryChanges = {
  entries: ItineraryChangeEntry[];
  activityStatuses: Map<DayRow, ActivityChangeStatus>;
  changedDays: Set<number>;
  changedAccommodations: Set<number>;
};

const normalize = (value?: string | null): string => (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const getPartOfTheDayOrder = (partOfTheDay: PartOfTheDay): number => {
  const index = allPartOfTheDay.indexOf(partOfTheDay);

  return index === -1 ? allPartOfTheDay.length : index;
};

const getActivityDetails = (activity: DayRow): string =>
  [
    normalize(activity.activity_description),
    normalize(activity.recommendation),
    normalize(activity.location?.name),
    normalize(activity.location?.city),
    normalize(activity.booking_id),
  ].join('|');

const getDiningSignature = (dayPlan: DayPlan): string =>
  (dayPlan.dinning ?? [])
    .map((row) => [normalize(row.name), normalize(row.description), normalize(row.recommendation)].join('~'))
    .join('|');

type OrderedEntry = { order: number; entry: ItineraryChangeEntry };

const compareDay = (
  previousDay: DayPlan,
  currentDay: DayPlan,
  activityStatuses: Map<DayRow, ActivityChangeStatus>
): ItineraryChangeEntry[] => {
  const ordered: OrderedEntry[] = [];
  const unmatched = [...previousDay.activities];
  const pending: DayRow[] = [];

  if (normalize(previousDay.title) !== normalize(currentDay.title)) {
    ordered.push({
      order: -1,
      entry: { kind: 'day_title', day: currentDay.day, title: currentDay.title, previousTitle: previousDay.title },
    });
  }

  currentDay.activities.forEach((activity) => {
    const titleIndex = unmatched.findIndex(
      (row) => normalize(row.activity_title) === normalize(activity.activity_title)
    );

    if (titleIndex === -1) {
      pending.push(activity);
      return;
    }

    const [match] = unmatched.splice(titleIndex, 1);
    const order = getPartOfTheDayOrder(activity.part_of_the_day);

    if (match.part_of_the_day !== activity.part_of_the_day) {
      activityStatuses.set(activity, 'moved');
      ordered.push({
        order,
        entry: {
          kind: 'activity_moved',
          day: currentDay.day,
          partOfTheDay: activity.part_of_the_day,
          previousPartOfTheDay: match.part_of_the_day,
          title: activity.activity_title,
        },
      });
      return;
    }

    if (getActivityDetails(match) !== getActivityDetails(activity)) {
      activityStatuses.set(activity, 'refined');
      ordered.push({
        order,
        entry: {
          kind: 'activity_refined',
          day: currentDay.day,
          partOfTheDay: activity.part_of_the_day,
          title: activity.activity_title,
        },
      });
    }
  });

  pending.forEach((activity) => {
    const order = getPartOfTheDayOrder(activity.part_of_the_day);
    let index = unmatched.findIndex(
      (row) => row.item_type === activity.item_type && row.part_of_the_day === activity.part_of_the_day
    );

    if (index === -1 && activity.item_type !== 'activity') {
      index = unmatched.findIndex((row) => row.item_type === activity.item_type);
    }

    if (index === -1) {
      activityStatuses.set(activity, 'added');
      ordered.push({
        order,
        entry: {
          kind: 'activity_added',
          day: currentDay.day,
          partOfTheDay: activity.part_of_the_day,
          title: activity.activity_title,
        },
      });
      return;
    }

    const [match] = unmatched.splice(index, 1);

    activityStatuses.set(activity, 'replaced');
    ordered.push({
      order,
      entry: {
        kind: 'activity_replaced',
        day: currentDay.day,
        partOfTheDay: activity.part_of_the_day,
        title: activity.activity_title,
        previousTitle: match.activity_title,
      },
    });
  });

  unmatched.forEach((activity) => {
    ordered.push({
      order: getPartOfTheDayOrder(activity.part_of_the_day),
      entry: {
        kind: 'activity_removed',
        day: currentDay.day,
        partOfTheDay: activity.part_of_the_day,
        title: activity.activity_title,
      },
    });
  });

  if (getDiningSignature(previousDay) !== getDiningSignature(currentDay)) {
    ordered.push({ order: allPartOfTheDay.length + 1, entry: { kind: 'day_dining', day: currentDay.day } });
  }

  if (normalize(previousDay.local_secret) !== normalize(currentDay.local_secret)) {
    ordered.push({ order: allPartOfTheDay.length + 2, entry: { kind: 'day_local_secret', day: currentDay.day } });
  }

  return ordered.sort((a, b) => a.order - b.order).map(({ entry }) => entry);
};

const compareAccommodations = (
  previous: ItineraryData,
  current: ItineraryData
): { entries: ItineraryChangeEntry[]; changed: Set<number> } => {
  const previousStays = getAccommodationStays(previous);
  const entries: ItineraryChangeEntry[] = [];
  const changed = new Set<number>();

  getAccommodationStays(current).forEach((currentStay, index) => {
    if (!currentStay.place) return;

    const previousStay = previousStays[index];

    if (normalize(previousStay?.place) !== normalize(currentStay.place)) {
      entries.push({
        kind: 'accommodation_replaced',
        place: currentStay.place,
        previousPlace: previousStay?.place ?? '',
      });
      changed.add(index);

      return;
    }

    if (normalize(previousStay?.place_description) !== normalize(currentStay.place_description)) {
      entries.push({ kind: 'accommodation_refined', place: currentStay.place });
      changed.add(index);
    }
  });

  return { entries, changed };
};

const getItineraryChanges = (
  previous: ItineraryData | null | undefined,
  current: ItineraryData | null | undefined
): ItineraryChanges | null => {
  if (!previous || !current) return null;

  const entries: ItineraryChangeEntry[] = [];
  const activityStatuses = new Map<DayRow, ActivityChangeStatus>();
  const changedDays = new Set<number>();
  const previousDays = new Map(previous.per_day_plan.map((dayPlan) => [dayPlan.day, dayPlan]));
  const currentDays = new Map(current.per_day_plan.map((dayPlan) => [dayPlan.day, dayPlan]));

  const accommodationChanges = compareAccommodations(previous, current);

  entries.push(...accommodationChanges.entries);

  current.per_day_plan.forEach((currentDay) => {
    const previousDay = previousDays.get(currentDay.day);

    if (!previousDay) {
      currentDay.activities.forEach((activity) => activityStatuses.set(activity, 'added'));
      entries.push({ kind: 'day_added', day: currentDay.day, title: currentDay.title });
      changedDays.add(currentDay.day);
      return;
    }

    const dayEntries = compareDay(previousDay, currentDay, activityStatuses);

    if (dayEntries.length === 0) return;

    entries.push(...dayEntries);
    changedDays.add(currentDay.day);
  });

  previous.per_day_plan.forEach((previousDay) => {
    if (currentDays.has(previousDay.day)) return;

    entries.push({ kind: 'day_removed', day: previousDay.day, title: previousDay.title });
  });

  if (entries.length === 0) return null;

  return {
    entries,
    activityStatuses,
    changedDays,
    changedAccommodations: accommodationChanges.changed,
  };
};

export default getItineraryChanges;
